import type { CommandId, EnvironmentApi, EnvironmentId } from "@cmmd-forge/contracts";

import { ensureEnvironmentProviderAuthority } from "../environments/runtime/service";
import {
  markRuntimeExecutionIntentDispatched,
  type RuntimeExecutionIntentAction,
  type RuntimeExecutionIntentWaitHandler,
} from "../environments/runtime/runtimeExecutionIntentApi";
import { prepareThreadExecutionIntentEnvironmentApi } from "./threadExecutionIntentEnvironmentApi";

export interface ThreadExecutionIntentActionDependencies {
  readonly prepare: (
    environmentId: EnvironmentId,
    intent: {
      readonly runtimeId?: string;
      readonly projectId: number | null;
      readonly action: RuntimeExecutionIntentAction;
      readonly targetId: string | null;
      readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
      readonly dispatchIdentity: CommandId;
      readonly signal?: AbortSignal;
    },
  ) => Promise<{
    readonly api: EnvironmentApi | null;
    readonly intent: {
      readonly id: string;
      readonly runtimeId: string;
      readonly status: string;
    } | null;
  }>;
  readonly acknowledge: (input: {
    readonly runtimeId: string;
    readonly intentId: string;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  }) => Promise<unknown>;
  readonly ensureProviderAuthority: (environmentId: EnvironmentId) => Promise<void>;
}

const DEFAULT_DEPENDENCIES: ThreadExecutionIntentActionDependencies = {
  prepare: prepareThreadExecutionIntentEnvironmentApi,
  acknowledge: markRuntimeExecutionIntentDispatched,
  ensureProviderAuthority: ensureEnvironmentProviderAuthority,
};

interface PreparedIntent {
  readonly id: string;
  readonly runtimeId: string;
  readonly status: string;
}

interface ExecutionDispatchOptions<T> {
  readonly wasAccepted?: (value: T) => boolean;
  readonly replayDispatched?: boolean;
}

type ExecutionDispatchResult<T> =
  | { readonly kind: "dispatched"; readonly value: T }
  | { readonly kind: "already-dispatched" };

async function replayAcceptedDispatch<T>(input: {
  readonly api: EnvironmentApi | null;
  readonly dispatch: (api: EnvironmentApi) => Promise<T>;
  readonly replay: boolean;
}): Promise<ExecutionDispatchResult<T>> {
  if (!input.replay) return { kind: "already-dispatched" };
  if (input.api === null) {
    throw new Error("The accepted Environment action no longer has a live connection.");
  }
  return { kind: "dispatched", value: await input.dispatch(input.api) };
}

async function ensureActionAuthority(input: {
  readonly action: RuntimeExecutionIntentAction;
  readonly environmentId: EnvironmentId;
  readonly dependencies: ThreadExecutionIntentActionDependencies;
}): Promise<void> {
  if (input.action !== "send") return;
  await input.dependencies.ensureProviderAuthority(input.environmentId);
}

async function acknowledgeAcceptedDispatch<T>(input: {
  readonly dependencies: ThreadExecutionIntentActionDependencies;
  readonly dispatchIdentity: CommandId;
  readonly intent: PreparedIntent | null;
  readonly signal?: AbortSignal;
  readonly value: T;
  readonly wasAccepted?: (value: T) => boolean;
}): Promise<void> {
  if (!input.intent || !(input.wasAccepted?.(input.value) ?? true)) return;
  await input.dependencies.acknowledge({
    runtimeId: input.intent.runtimeId,
    intentId: input.intent.id,
    dispatchIdentity: input.dispatchIdentity,
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

async function dispatchPreparedAction<T>(input: {
  readonly api: EnvironmentApi | null;
  readonly dependencies: ThreadExecutionIntentActionDependencies;
  readonly dispatch: (api: EnvironmentApi) => Promise<T>;
  readonly environmentId: EnvironmentId;
  readonly intent: {
    readonly action: RuntimeExecutionIntentAction;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  };
  readonly preparedIntent: PreparedIntent | null;
  readonly options?: ExecutionDispatchOptions<T>;
}): Promise<ExecutionDispatchResult<T>> {
  if (input.preparedIntent?.status === "dispatched") {
    return replayAcceptedDispatch({
      api: input.api,
      dispatch: input.dispatch,
      replay: input.options?.replayDispatched === true,
    });
  }
  if (input.api === null) {
    throw new Error("The prepared Environment action did not return a live connection.");
  }
  await ensureActionAuthority({
    action: input.intent.action,
    environmentId: input.environmentId,
    dependencies: input.dependencies,
  });
  const value = await input.dispatch(input.api);
  await acknowledgeAcceptedDispatch({
    dependencies: input.dependencies,
    dispatchIdentity: input.intent.dispatchIdentity,
    intent: input.preparedIntent,
    value,
    ...(input.intent.signal ? { signal: input.intent.signal } : {}),
    ...(input.options?.wasAccepted ? { wasAccepted: input.options.wasAccepted } : {}),
  });
  return { kind: "dispatched", value };
}

export interface PreparedThreadExecutionIntentAction {
  readonly api: EnvironmentApi | null;
  readonly alreadyDispatched: boolean;
  readonly intent: PreparedIntent | null;
  readonly dispatch: <T>(
    dispatch: (api: EnvironmentApi) => Promise<T>,
    options?: ExecutionDispatchOptions<T>,
  ) => Promise<ExecutionDispatchResult<T>>;
}

export async function prepareThreadExecutionIntentAction(
  environmentId: EnvironmentId,
  intent: {
    readonly runtimeId?: string;
    readonly projectId: number | null;
    readonly action: RuntimeExecutionIntentAction;
    readonly targetId: string | null;
    readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  },
  dependencies: ThreadExecutionIntentActionDependencies = DEFAULT_DEPENDENCIES,
): Promise<PreparedThreadExecutionIntentAction> {
  const prepared = await dependencies.prepare(environmentId, intent);
  return {
    api: prepared.api,
    alreadyDispatched: prepared.intent?.status === "dispatched",
    intent: prepared.intent,
    dispatch: <T>(
      dispatch: (api: EnvironmentApi) => Promise<T>,
      options?: ExecutionDispatchOptions<T>,
    ) =>
      dispatchPreparedAction({
        api: prepared.api,
        dependencies,
        dispatch,
        environmentId,
        intent,
        preparedIntent: prepared.intent,
        ...(options ? { options } : {}),
      }),
  };
}

export async function runThreadExecutionIntentAction<T>(
  environmentId: EnvironmentId,
  intent: {
    readonly runtimeId?: string;
    readonly projectId: number | null;
    readonly action: RuntimeExecutionIntentAction;
    readonly targetId: string | null;
    readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  },
  action: {
    readonly dispatch: (api: EnvironmentApi) => Promise<T>;
    readonly wasAccepted?: (value: T) => boolean;
  },
  dependencies: ThreadExecutionIntentActionDependencies = DEFAULT_DEPENDENCIES,
): Promise<
  { readonly kind: "dispatched"; readonly value: T } | { readonly kind: "already-dispatched" }
> {
  const prepared = await prepareThreadExecutionIntentAction(environmentId, intent, dependencies);
  const options = action.wasAccepted ? { wasAccepted: action.wasAccepted } : undefined;
  return prepared.dispatch(action.dispatch, options);
}
